import { FormEvent, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { TemplateFormProps } from '../components/template-form/template-form';
import { MESSAGE_DELIVERY_METHODS } from '../data/message-delivery-methods';
import { Audience } from '../types/audience';
import { Location } from '../types/location';
import { Template } from '../types/template';

export const useTemplateForm = (
  { dataAccess, values, onSave, onDelete }: TemplateFormProps,
  form: UseFormReturn<Template>,
) => {
  const [messageVariables, setMessageVariables] = useState<{ label: string; value: string }[]>([]);
  const [employeeAudienceOptions, setEmployeeAudienceOptions] = useState<Audience[]>([]);
  const [visitorAudienceOptions, setVisitorAudienceOptions] = useState<Audience[]>([]);
  const [locationOptions, setLocationOptions] = useState<Location[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { errors, isValid } = form.formState;

  const isDisabled = useMemo(() => {
    return Object.keys(errors).length || !isValid;
  }, [errors, isValid]);

  const loadState = async () => {
    const [audiencesResult, locationsResult, messageVariablesResult] = await Promise.all([
      dataAccess.getAudiences(),
      dataAccess.getLocations(),
      dataAccess.getMessageVariables(),
    ]);

    setEmployeeAudienceOptions(audiencesResult.employees || []);
    setVisitorAudienceOptions(audiencesResult.visitors || []);
    setLocationOptions(locationsResult || []);
    setMessageVariables(messageVariablesResult || []);
  };

  const fillForm = () => {
    form.reset({
      name: values?.name || '',
      title: values?.title || '',
      message: values?.message || '',
      category: values?.category || undefined,
      defaultChannels: values?.defaultChannels || MESSAGE_DELIVERY_METHODS.map((d) => d.value),
      defaultEmployeeAudiences: values?.defaultEmployeeAudiences || [],
      defaultVisitorAudiences: values?.defaultVisitorAudiences || [],
      locations: values?.locations || [],
      markAsSafe: values?.markAsSafe || false,
      published: values?.published || true,
    });
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      const formValues = form.getValues();
      await onSave(formValues);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    try {
      setIsDeleting(true);
      await onDelete();
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    messageVariables,
    employeeAudienceOptions,
    visitorAudienceOptions,
    locationOptions,
    isDisabled,
    isSaving,
    isDeleting,
    fillForm,
    loadState,
    handleDelete,
    handleSave,
  };
};
